import {TicketStatusCategory} from '@frontapp/ticketing';
import {compact, concat} from 'lodash';
import React, {useCallback, useMemo} from 'react';

import {getInboxes} from '../../../../../../libs/inboxes/store/src/inboxesSelectors';
import {ticketStatusCategoryNames} from '../../../../../../libs/inboxes/ui/src/ticketing/ticketStatus';
import {Trans} from '../../../../../../libs/shared/core/src/helpers/internationalization/react';
import {UnreachableError} from '../../../../../../libs/shared/core/src/helpers/types/customErrors';
import {CancelToken} from '../../../../../../libs/shared/core/src/helpers/utilities/asyncCancelHelpers';
import {TagBehaviorsEnum} from '../../../../../../libs/shared/core/src/models/api/commonTagModel';
import {InboxModel} from '../../../../../../libs/shared/core/src/models/api/inboxModel';
import {fetchApiAsync} from '../../../../../../libs/shared/core/src/services/http/http';
import {getCompanyHasTicketStatusesFeature} from '../../../../../../libs/shared/core/src/store/data/company/commonCompanySelectors';
import {AppSelector, createSelector} from '../../../../../../srcCommon/helpers/reselectHelpers';
import {ModifiersEnum} from '../../../../../../srcCommon/inboxes/search/searchModifiersHelpers';
import {useAppSelector} from '../../../../../helpers/appReduxHelpers';
import {buildTagSearchUrl, getTagAliasDuplicates} from '../../../../../helpers/models/tagHelpers';
import {AccountModel} from '../../../../../models/api/accountModel';
import {ApiSearchTags, SearchTagModel} from '../../../../../models/api/searchTagModel';
import {InboxesSearchLocationsEnum} from '../../../../../models/url/models/inboxesUrlModel';
import {getCompany} from '../../../../../store/data/company/companySelectors';
import {
  getFlatInboxes,
  getUserParticipatingInboxes,
  makeGetInboxesByNamespace,
} from '../../../../../store/data/inboxes/inboxesSelectors';
import {
  getPinnedWorkspaceInboxes,
  makeGetCustomViewNamespace,
  makeGetInboxesByLensId,
  makeGetInboxesByTeammateId,
  makeGetTeamInboxesByTeamId,
  makeHasNonTicketingInboxes,
  makeHasTicketingInboxes,
} from '../../../../../store/data/search/searchSelectors';
import {
  InboxesParameters,
  InboxesParametersTypesEnum,
} from '../../../../../store/panels/inboxes/parameters/inboxesParameters';
import {AccountDropdownItem} from '../../../../common/dropdowns/accountDropdownItem';
import {TagDropdownItem} from '../../../../common/dropdowns/tagDropdownItem';
import {TicketStatusIndicator} from '../../../ticketing/ticketStatusIndicator';
import {
  InputValue,
  SyncDropdownInputCustomIconProps,
  SyncDropdownInputProps,
} from './inboxesSearchQueryBuilderTypes';

/*
 * Helpers.
 */

function replaceDoubleQuotes(value: string) {
  return value.replace(/"/g, '');
}

export function maybeWrapWithDoubleQuotes(value: string) {
  if (/\s/.test(value)) {
    // Remove quotes added in the input as they would mess up with `SearchToken` value.
    return `"${replaceDoubleQuotes(value)}"`;
  }

  return replaceDoubleQuotes(value);
}

export function maybeRemoveDoubleQuotes(value: undefined): undefined;
export function maybeRemoveDoubleQuotes(value: string): string;
export function maybeRemoveDoubleQuotes(value: string | undefined): string | undefined;
export function maybeRemoveDoubleQuotes(value: string | undefined): string | undefined {
  if (!value) {
    return value;
  }

  if (value.length >= 2 && value[0] === '"' && value[value.length - 1] === '"') {
    return value.substring(1, value.length - 1);
  }

  return value;
}

/*
 * Tags.
 */

export const getFetchTagsAsync = createSelector(
  [getCompany],
  (company) =>
    async function fetchAsync(query: string, tags: ReadonlyArray<SearchTagModel>, cancelToken: CancelToken) {
      const url = buildTagSearchUrl(company, query, {
        offset: tags.length,
        behaviors: [TagBehaviorsEnum.CAN_PARENT],
        source: 'query-builder-tags',
      });

      const newTags = await fetchApiAsync(ApiSearchTags, url, {cancelToken});
      return {
        data: concat(tags.length > 0 ? tags : [], newTags.tags),
        canRequestMore: newTags.tags.length === newTags.limit,
      };
    },
);

export function makeRenderTagRow(
  selectedTagAlias: InputValue | undefined,
  onSelect: (value: InputValue) => void,
) {
  return function (
    searchTag: SearchTagModel,
    index: number,
    searchTags: ReadonlyArray<SearchTagModel | null>,
    isSearching?: boolean,
  ) {
    const tag = searchTag[0];
    const tagAliasDuplicates = getTagAliasDuplicates(searchTags);

    const nestingLevel = isSearching ? 0 : searchTag.length - 1;
    const tagsPath = isSearching ? searchTag : [];

    return (
      <TagDropdownItem
        tag={tag}
        tagsPath={tagsPath}
        key={tag.id}
        index={index}
        isChecked={tag.alias === selectedTagAlias?.value}
        nestingLevel={nestingLevel}
        shouldShowDeduplication={tagAliasDuplicates.has(tag.alias)}
        onSelect={(e) => onSelect({modifier: ModifiersEnum.TAG, value: tag.alias})}
      />
    );
  };
}

export function makeRenderAccountRow(
  selectedAccountName: InputValue | undefined,
  onSelect: (value: InputValue) => void,
) {
  return function (searchAccount: AccountModel) {
    const isChecked = selectedAccountName?.value === searchAccount.name;

    // alias values should always be set, but fallback to name if not provided.
    const value = searchAccount.alias ?? searchAccount.name;

    return (
      <AccountDropdownItem
        key={searchAccount.id}
        account={searchAccount}
        isChecked={isChecked}
        onSelect={(e) => onSelect({modifier: ModifiersEnum.ACCOUNT, value})}
      />
    );
  };
}

/*
 * Status.
 */

const standardConversationStatusDropdownItems: ReadonlyArray<SyncDropdownInputProps> = [
  {
    icon: 'inboxSmall',
    textLabel: 'Open',
    inputValue: {value: 'open'},
    label: <Trans id="search-query-builder-status-open">Open</Trans>,
  },
  {
    icon: 'archiveSmall',
    textLabel: 'Archived',
    inputValue: {value: 'archived'},
    label: <Trans id="search-query-builder-status-archived">Archived</Trans>,
  },
];

const ticketingConversationStatusDropdownItems: ReadonlyArray<SyncDropdownInputCustomIconProps> = [
  {
    icon: <TicketStatusIndicator category={TicketStatusCategory.OPEN} />,
    label: ticketStatusCategoryNames[TicketStatusCategory.OPEN],
    textLabel: 'Open',
    inputValue: {
      value: TicketStatusCategory.OPEN,
    },
  },
  {
    icon: <TicketStatusIndicator category={TicketStatusCategory.WAITING} />,
    label: ticketStatusCategoryNames[TicketStatusCategory.WAITING],
    textLabel: 'Waiting',
    inputValue: {
      value: TicketStatusCategory.WAITING,
    },
  },
  {
    icon: <TicketStatusIndicator category={TicketStatusCategory.RESOLVED} />,
    label: ticketStatusCategoryNames[TicketStatusCategory.RESOLVED],
    textLabel: 'Resolved',
    inputValue: {
      value: TicketStatusCategory.RESOLVED,
    },
  },
];

const sharedConversationStatusDropdownItems: ReadonlyArray<SyncDropdownInputProps> = [
  {
    icon: 'snoozeSmall',
    textLabel: 'Snoozed',
    inputValue: {value: 'snoozed'},
    label: <Trans id="search-query-builder-status-snoozed">Snoozed</Trans>,
  },
  {
    icon: 'assignSmall',
    textLabel: 'Assigned',
    inputValue: {value: 'assigned'},
    label: <Trans id="search-query-builder-status-assigned">Assigned</Trans>,
  },
  {
    icon: 'unassignedSmall',
    textLabel: 'Unassigned',
    inputValue: {value: 'unassigned'},
    label: <Trans id="search-query-builder-status-unassigned">Unassigned</Trans>,
  },
  {
    icon: 'teammatesSmall',
    textLabel: 'Shared with me',
    inputValue: {value: 'shared-with-me'},
    label: <Trans id="search-query-builder-status-shared-with-me">Shared with me</Trans>,
  },
  {
    icon: 'newMessageSmall',
    textLabel: 'Unread',
    inputValue: {value: 'unread'},
    label: <Trans id="search-query-builder-status-unread">Unread</Trans>,
  },

  {
    icon: 'repliedSmall',
    textLabel: 'Unreplied',
    inputValue: {value: 'unreplied'},
    label: <Trans id="search-query-builder-status-unreplied">Unreplied</Trans>,
  },
  {
    icon: 'subscribeSmall',
    textLabel: 'Subscribed',
    inputValue: {value: 'subscribed'},
    label: <Trans id="search-query-builder-status-subscribed">Subscribed</Trans>,
  },
  {
    icon: 'muteSmall',
    textLabel: 'Unsubscribed',
    inputValue: {value: 'unsubscribed'},
    label: <Trans id="search-query-builder-status-unsubscribed">Unsubscribed</Trans>,
  },
  {
    icon: 'discussionSmall',
    textLabel: 'Discussion',
    inputValue: {value: 'discussion'},
    label: <Trans id="search-query-builder-status-discussion">Discussion</Trans>,
  },
  {
    icon: 'starSmall',
    textLabel: 'Starred',
    inputValue: {value: 'starred'},
    label: <Trans id="search-query-builder-status-starred">Starred</Trans>,
  },
  {
    icon: 'spamSmall',
    textLabel: 'Spam',
    inputValue: {value: 'spam'},
    label: <Trans id="search-query-builder-status-spam">Spam</Trans>,
  },
  {
    icon: 'trashSmall',
    textLabel: 'Trashed',
    inputValue: {value: 'trashed'},
    label: <Trans id="search-query-builder-status-trashed">Trashed</Trans>,
  },
];

export const allStatusDropdownItems: ReadonlyArray<SyncDropdownInputCustomIconProps> = [
  ...ticketingConversationStatusDropdownItems,
  ...standardConversationStatusDropdownItems,
  ...sharedConversationStatusDropdownItems,
];

export function useStatusDropdownItems({
  selectedLocation,
  parameters,
}: {
  selectedLocation: InboxesSearchLocationsEnum;
  parameters?: InboxesParameters;
}) {
  const hasTicketingFeatureEnabled = useAppSelector(getCompanyHasTicketStatusesFeature);

  const getSelectedInboxes = useMemo(
    () => computeInboxesSelector(selectedLocation, parameters),
    [parameters, selectedLocation],
  );
  const {hasNonTicketingEnabledInbox, hasTicketingEnabledInbox} = useIncludesInboxTypes(getSelectedInboxes);

  const hasMixedInboxes = hasTicketingEnabledInbox && hasNonTicketingEnabledInbox;

  const shouldRenderSeparator = useCallback(
    (index: number) => {
      if (!hasTicketingFeatureEnabled || !hasMixedInboxes) {
        return false;
      }

      // Show separator after ticket statuses and after standard statuses.
      return (
        index === ticketingConversationStatusDropdownItems.length ||
        index ===
          ticketingConversationStatusDropdownItems.length + standardConversationStatusDropdownItems.length
      );
    },
    [hasMixedInboxes, hasTicketingFeatureEnabled],
  );

  // Show standard statuses if ticket statuses are disabled or if there are no inboxes
  if (!hasTicketingFeatureEnabled || (!hasTicketingEnabledInbox && !hasNonTicketingEnabledInbox)) {
    return {
      statuses: [...standardConversationStatusDropdownItems, ...sharedConversationStatusDropdownItems],
      shouldRenderSeparator,
    };
  }

  return {
    statuses: [
      ...(hasTicketingEnabledInbox ? ticketingConversationStatusDropdownItems : []),
      ...(hasNonTicketingEnabledInbox ? standardConversationStatusDropdownItems : []),
      ...sharedConversationStatusDropdownItems,
    ],
    shouldRenderSeparator,
  };
}

/*
 * Hooks.
 */

const useIncludesInboxTypes = (getSelectedInboxes: AppSelector<ReadonlyArray<InboxModel>>) => {
  const getHasTicketingInbox = useMemo(
    () => makeHasTicketingInboxes(getSelectedInboxes),
    [getSelectedInboxes],
  );
  const getHasNonTicketingInbox = useMemo(
    () => makeHasNonTicketingInboxes(getSelectedInboxes),
    [getSelectedInboxes],
  );
  const hasTicketingEnabledInbox = useAppSelector(getHasTicketingInbox);
  const hasNonTicketingEnabledInbox = useAppSelector(getHasNonTicketingInbox);

  return {
    hasTicketingEnabledInbox,
    hasNonTicketingEnabledInbox,
  };
};

/*
 * Helpers.
 */

function computeInboxesSelector(
  selectedLocation: InboxesSearchLocationsEnum,
  parameters?: InboxesParameters,
): AppSelector<ReadonlyArray<InboxModel>> {
  if (!parameters) {
    return noInboxesSelector;
  }

  switch (selectedLocation) {
    case InboxesSearchLocationsEnum.GLOBAL:
      return getFlatInboxes;

    case InboxesSearchLocationsEnum.PINNED_IN_WORKSPACE:
      return getPinnedWorkspaceInboxes;

    case InboxesSearchLocationsEnum.LOCAL: {
      const {type} = parameters;

      switch (type) {
        case InboxesParametersTypesEnum.USER:
          return getUserParticipatingInboxes;

        case InboxesParametersTypesEnum.INBOX:
        case InboxesParametersTypesEnum.TEAMMATE_INBOX:
          return makeGetCurrentInbox(parameters.inboxId);

        case InboxesParametersTypesEnum.TEAM:
          return makeGetTeamInboxesByTeamId(parameters.teamId);

        case InboxesParametersTypesEnum.TEAM_LENS:
        case InboxesParametersTypesEnum.TEAMMATE_LENS:
          return makeGetInboxesByLensId(parameters.lensId);

        case InboxesParametersTypesEnum.TEAMMATE:
        case InboxesParametersTypesEnum.TEAMMATE_MESSAGES:
          return makeGetInboxesByTeammateId(parameters.teammateId);

        case InboxesParametersTypesEnum.GLOBAL:
          return getFlatInboxes;

        case InboxesParametersTypesEnum.VIEW:
        case InboxesParametersTypesEnum.TEAMMATE_VIEW:
          return makeGetInboxesByNamespace(makeGetCustomViewNamespace(parameters.viewId));

        case InboxesParametersTypesEnum.AUDIT_TRAIL: {
          return noInboxesSelector;
        }
        default: {
          throw new UnreachableError(type);
        }
      }
    }
    default: {
      throw new UnreachableError(selectedLocation);
    }
  }
}

function makeGetCurrentInbox(inboxId: number | undefined): AppSelector<ReadonlyArray<InboxModel>> {
  return createSelector([getInboxes], (inboxes) => (inboxId ? compact([inboxes[inboxId]]) : []));
}

function noInboxesSelector(): ReadonlyArray<InboxModel> {
  return [];
}

/*
 * Message type.
 */

export const searchScopeDropdownItems = (): ReadonlyArray<SyncDropdownInputProps> => [
  {
    icon: 'message',
    inputValue: {value: 'messages'},
    label: <Trans id="search-query-builder-message-type-messages">Messages</Trans>,
    textLabel: 'Messages',
  },
  {
    icon: 'channelChatOutlineSmall',
    inputValue: {value: 'comments'},
    label: <Trans id="search-query-builder-message-type-comments">Comments</Trans>,
    textLabel: 'Comments',
  },
  {
    icon: 'attachment',
    inputValue: {value: 'attachments'},
    label: <Trans id="search-query-builder-message-type-attachments">Attachments</Trans>,
    textLabel: 'Attachments',
  },
];
